/**
 * Example slider configuration object.
 *
 * Represents a UI slider with orientation rules, default layout settings,
 * and nested child elements such as fill sprites and masks.
 *
 * Structure:
 * - ID: Unique identifier for the slider.
 * - type: Component type ("slider").
 * - default: Base transform settings (position, scale).
 * - orientations: Determines how the slider is displayed for each layout.
 *   Useful so the slider knows whether to behave vertically or horizontally
 *   depending on portrait/landscape and device type.
 *   A slider set to horizontal will fill left to the right.
 *   A slider set to vertical will fill bottom to top.
 *
 * - children: Nested display objects belonging to the slider.
 *   - "fill": The visual sprite or movieclip representing the slider fill.
 *       If a child object is named "fill", the slider automatically uses it.
 *
 *       - children:
 *           - "mask": A graphics object used as the fill mask.
 *             Useful for defining offsets based on size/shape.
 *           - If a child object of "fill" is named "mask", the slider automatically uses it.
 *
 * Example:
 * {
 *   "ID": "BonusBar",
 *   "type": "slider",
 *   "default": { "position": { "x": 52, "y": 340 }, "scale": 1 },
 *   "orientations": {
 *     "portrait_default": "vertical",
 *     "landscape_mobile": "vertical",
 *     "portrait_mobile": "vertical",
 *     "default": "vertical"
 *   },
 *   "children": [
 *     {
 *       "ID": "fill",
 *       "type": "movie",
 *       "definition": "bonusBarFill",
 *       "anchor": 0.5,
 *       "default": { "position": { "x": -2, "y": 13 }, "visible": true },
 *       "loop": true,
 *       "children": [
 *         {
 *           "ID": "mask",
 *           "type": "graphics",
 *           "default": { "position": { "x": -26, "y": 246 } }
 *         }
 *       ]
 *     }
 *   ]
 * }
 *
 * The Slider Class creates a fully dynanmic 'bar' object the mask of which is scaled automatically
 * so that by using the setSliderFill function, the bar can appear to fill to a certain percentage.
 * The class handles 'bars' scaling from bottom to top (in vertical configuration) and left to right (in horizontal configuration).
 * Optionally the slider's fill can be animated. See setSliderFill for more information.
 */
(function () {
  class Slider extends PIXI.Container {
    constructor(json) {
      super();

      this.fill =
        json !== undefined && json.fill !== undefined
          ? resourceManager.get(json.fill)
          : undefined;
      this.orientations = json.orientations;
      if (this.orientations === undefined) {
        console.error(
          this.name +
            " slider has no orientations available. Please define these in the json. Assuming vertical default."
        );
      }
      messageBus.subscribe(
        "spinner:GameConfigEvent",
        this.onGameConfigEvent.bind(this)
      );
      Reflex.registerEventHandler(
        "orientationChange",
        this.onOrientationChange.bind(this)
      );
    }

    onGameConfigEvent() {
      this.orientation = "vertical";
      this.percent = 0;
      this.fill =
        this.fill === undefined
          ? this.findChild(this.children, "fill")
          : this.fill;

      if (this.fill !== undefined) {
        this.mask = this.findChild(this.children, "mask");
        if (this.mask != undefined) {
          this.fill.addChild(this.mask);
        } else {
          this.mask = this.findChild(this.fill.children, "mask");
        }
      } else {
        console.error("SLIDER: FAILED TO FIND FILL ELEMENT " + this.name);
      }
      this.onOrientationChange();
    }

    findChild(parent, name) {
      const child = parent.find(function (child) {
        return child.Props.ID === name;
      });
      return child;
    }

    onOrientationChange() {
      switch (layoutController.getCurrentLayout()) {
        case "landscape_default": {
          this.changeOrientation(this.orientations.landscape_default);
          break;
        }

        case "portrait_default": {
          this.changeOrientation(this.orientations.portrait_default);
          break;
        }

        case "portrait_mobile": {
          this.changeOrientation(this.orientations.portrait_mobile);
          break;
        }

        case "landscape_mobile": {
          this.changeOrientation(this.orientations.landscape_mobile);
          break;
        }
        case "default": {
          this.changeOrientation(this.orientations.default);
          break;
        }
      }
    }

    /**
     * Changes the slider's current fill.
     * @param {float} percentage - Should be between 0 - 1. 0.25 for example is 25% filled.
     * @param {boolean} animate - If the slider animates from the current fill to the new value.
     * @param {float} duration - Duration of the slider animation, does nothing if animate is false.
     * @param {Function} callback - the function that runs when the fill has been set to the new value.
     */
    setSliderFill(percentage, animate = false, duration = 0.3, callback) {
      if (animate) {
        const self = this;
        this.adjustMaskTween = gsap.fromTo(
          self,
          {
            ease: "none",
            percent: self.percent,
            onStart: () => {
              self.adjustMask(self.percent);
            },
          },
          {
            duration: duration,
            ease: "none",
            percent: percentage,
            onUpdate: () => {
              self.adjustMask(self.percent);
            },
            onComplete: () => {
              self.percent = percentage;
              self.adjustMask(self.percent);
              if (callback !== undefined) {
                callback();
              }
            },
          }
        );
      } else {
        this.percent = percentage;
        this.adjustMask(this.percent);
        if (callback !== undefined) {
          callback();
        }
      }
    }

    adjustMask(percentage) {
      this.mask.clear();
      this.mask.beginFill(0xffffff);
      if (this.orientation === "horizontal") {
        this.mask.drawRect(
          0,
          0,
          this.fill.width * percentage,
          -this.fill.height
        );
      } else {
        this.mask.drawRect(
          0,
          0,
          this.fill.width,
          -(this.fill.height * percentage)
        );
      }
      this.mask.endFill();
    }

    /**
     * Changes the slider's fill current orientation.
     * For instance, a slider set to horizontal will fill left to the right.
     * A slider set to vertical will fill bottom to top.
     * @param {string} orientation - the orientation to switch to.
     */
    changeOrientation(orientation) {
      if (orientation === undefined) {
        orientation =
          this.orientations.default !== undefined
            ? this.orientations.default
            : "vertical";
      }
      this.orientation = orientation;
      if (this.mask) {
        this.adjustMask(this.percent);
      }
    }
  }

  var creator = function (json) {
    return new Slider(json);
  };

  var initialiser = function (json) {};

  var filesRequired = function () {
    return [];
  };

  resourceManager.registerObjectType(
    "slider",
    creator,
    initialiser,
    filesRequired
  );
})();
